// Wall Mode data layer + MQTT sequence executor (React port of the Flutter
// wallFunctions.dart). Uses the same backend endpoints added for the Flutter app.

import {
  getWallPanels, addWallPanel, updateWallPanel, deleteWallPanel,
  addWallButton, updateWallButton, deleteWallButton,
  addWallButtonAction, deleteWallButtonActionsByButtonId,
} from '../../../../core/api/api';
import { publishCommand } from '../../../../core/utils/deviceController';

const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
};

const insertIdOf = (res) => {
  const d = res && res.data;
  const m = Array.isArray(d) ? d[0] : d;
  return m && (m.insertId != null) ? Number(m.insertId) : null;
};

export function normalizePanels(raw) {
  return asArray(raw)
    .map((p) => ({
      ...p,
      WallPanelId: Number(p.WallPanelId),
      ColumnsCount: Number(p.ColumnsCount) || 4,
      SortOrder: Number(p.SortOrder) || 0,
      Buttons: asArray(p.Buttons)
        .map((b) => ({
          ...b,
          WallButtonId: Number(b.WallButtonId),
          Width: Number(b.Width) || 1,
          Height: Number(b.Height) || 1,
          SortOrder: Number(b.SortOrder) || 0,
          TargetWallPanelId: Number(b.TargetWallPanelId) || 0,
          Actions: asArray(b.Actions)
            .map((a) => ({ ...a, StepOrder: Number(a.StepOrder) || 0, DelaySeconds: Number(a.DelaySeconds) || 0 }))
            .sort((x, y) => x.StepOrder - y.StepOrder),
        }))
        .sort((x, y) => x.SortOrder - y.SortOrder),
    }))
    .sort((a, b) => a.SortOrder - b.SortOrder);
}

// Scenes ride on the same wall tables (see src/pages/scenes/scenesApi.js): each
// room's scenes live in a hidden container panel named `__scene__<RoomID>`. Wall
// Mode must never surface those, so filter them out of every panel fetch here.
export const SCENE_PANEL_PREFIX = '__scene__';
const isScenePanel = (p) =>
  typeof p?.PanelName === 'string' && p.PanelName.startsWith(SCENE_PANEL_PREFIX);

export async function fetchPanels(userId) {
  try {
    const res = await getWallPanels(userId);
    return normalizePanels(res.data).filter((p) => !isScenePanel(p));
  } catch (e) {
    console.error('fetchPanels', e);
    return [];
  }
}

export async function createPanel(userId, panel) {
  const res = await addWallPanel({
    UserId: userId,
    PanelName: panel.PanelName,
    SortOrder: panel.SortOrder || 0,
    BackColor: panel.BackColor || '#101820',
    ColumnsCount: panel.ColumnsCount || 4,
  });
  return insertIdOf(res);
}

export const editPanel = (panelId, data) =>
  updateWallPanel(data, `WallPanelId = ${panelId}`);

// Delete a panel and all of its buttons + their action rows (no orphans).
// Accepts the normalized panel object so we know which buttons to clean up.
export async function removePanel(panel) {
  const panelId = panel.WallPanelId;
  const buttons = panel.Buttons || [];
  for (const b of buttons) {
    // eslint-disable-next-line no-await-in-loop
    await deleteWallButtonActionsByButtonId(b.WallButtonId);
  }
  await deleteWallButton(`WallPanelId = ${panelId}`);
  await deleteWallPanel(`WallPanelId = ${panelId}`);
}

// Save a button + its whole action sequence. Returns the button id.
export async function saveButton(userId, button) {
  const col = {
    WallPanelId: button.WallPanelId,
    UserId: userId,
    ButtonText: button.ButtonText || '',
    ButtonColor: button.ButtonColor || '#37474F',
    TextColor: button.TextColor || '#FFFFFF',
    IconName: button.IconName || '',
    ButtonType: button.ButtonType || 'action',
    TargetWallPanelId: button.ButtonType === 'navigate' ? (button.TargetWallPanelId || 0) : 0,
    Width: button.Width || 1,
    Height: button.Height || 1,
    // ⚠️ WAS HARDCODED TO 0,0 — the free-canvas designer never moved buttons,
    // so the position silently round-tripped as zero. A fixed grid stores its
    // slot in exactly these two columns, so every button would have landed in
    // the top-left slot and overwritten the last one.
    PosX: Number(button.PosX) || 0,
    PosY: Number(button.PosY) || 0,
    SortOrder: button.SortOrder || 0,
  };

  let buttonId = button.WallButtonId;
  if (!buttonId) {
    buttonId = insertIdOf(await addWallButton(col));
  } else {
    await updateWallButton(col, `WallButtonId = ${buttonId}`);
  }
  if (!buttonId) return null;

  await deleteWallButtonActionsByButtonId(buttonId);
  const steps = button.ButtonType === 'navigate' ? [] : (button.Actions || []);
  for (let i = 0; i < steps.length; i++) {
    const a = steps[i];
    // eslint-disable-next-line no-await-in-loop
    await addWallButtonAction({
      WallButtonId: buttonId,
      StepOrder: i + 1,
      ActionType: a.ActionType || 'mqtt',
      UniqueNumber: a.UniqueNumber || '',
      MqttMessage: a.MqttMessage || '',
      DelaySeconds: a.DelaySeconds || 0,
    });
  }
  return buttonId;
}

// Caption only. `saveButton` deletes and re-adds a button's action rows, so
// using it to correct a label would rewrite the sequence too — and a failure
// halfway would leave a button that no longer does anything. Relabelling must
// not be able to break a working tile.
export const setButtonText = (buttonId, text) =>
  updateWallButton({ ButtonText: text }, `WallButtonId = ${buttonId}`);

export const removeButton = (buttonId) =>
  deleteWallButtonActionsByButtonId(buttonId)
    .then(() => deleteWallButton(`WallButtonId = ${buttonId}`));

// ---- Sequence executor ----
const running = new Set();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export async function runSequence(userId, button) {
  const id = button.WallButtonId;
  if (running.has(id)) return;
  running.add(id);
  try {
    const steps = [...(button.Actions || [])].sort((a, b) => a.StepOrder - b.StepOrder);
    for (const step of steps) {
      if (step.ActionType === 'delay') {
        if (step.DelaySeconds > 0) await sleep(step.DelaySeconds * 1000);
      } else if (step.UniqueNumber && step.MqttMessage) {
        publishCommand(step.UniqueNumber, step.MqttMessage, userId);
      }
    }
  } finally {
    running.delete(id);
  }
}
